import { useState, useEffect, useMemo } from 'react';
import { useWebSockets } from 'src/contexts/webSocketsContext/websockets.context';
import { STATE, MODE } from '../config';
import { AssetType } from 'src/types/assets';
import { findBestMatchingTagInput } from '../utils/findBestMatchingTagInput';

type useValveDataArguments = {
  asset: AssetType;
};

export const useValveData = ({ asset }: useValveDataArguments) => {
  const [valveLiveData, setValveLiveData] = useState({
    mode: '',
    state: '',
  });
  const { messages } = useWebSockets();

  const gatewayInputs = useMemo(() => {
    if (!messages || !asset?.assignedTags) return undefined;

    if (Array.isArray(messages)) {
      return messages
        .map((msg: any) => {
          const matchingTags = asset.assignedTags?.filter(
            (tag) => tag.assignedGatewayId === msg?.data?.sourceId
          );
          return matchingTags && matchingTags.length > 0 ? msg.data.data : undefined;
        })
        .filter(Boolean)
        .flat();
    }

    if (messages?.data?.data) {
      return messages.data.data;
    }

    return undefined;
  }, [messages, asset?.assignedTags]);

  useEffect(() => {
    if (!asset || !asset.assignedTags) {
      setValveLiveData({
        mode: '',
        state: '',
      });
      return;
    }
    if (!gatewayInputs) return;

    const stateResult = findBestMatchingTagInput({
      assignedTags: asset.assignedTags,
      gatewayInputs,
      tagType: STATE,
    });

    const modeResult = findBestMatchingTagInput({
      assignedTags: asset.assignedTags,
      gatewayInputs,
      tagType: MODE,
    });

    setValveLiveData((prev) => {
      let next = { ...prev };

      if (stateResult?.tag && stateResult?.input) {
        const { tag, input } = stateResult;
        const newStateValue =
          Number(input.value) === Number(tag.tagIndicatorValue) ? tag.tagIndicator : '-';

        if (prev.state !== newStateValue) {
          next.state = newStateValue;
        }
      }

      if (modeResult?.tag && modeResult?.input) {
        const { tag, input } = modeResult;
        const newModeValue =
          Number(input.value) === Number(tag.tagIndicatorValue) ? tag.tagIndicator : '-';

        if (prev.mode !== newModeValue) {
          next.mode = newModeValue;
        }
      }

      if (next.state !== prev.state || next.mode !== prev.mode) {
        return next;
      }
      return prev;
    });
  }, [gatewayInputs, asset]);

  return {
    valveLiveData,
  };
};
